/**
 * Shared money / coin formatting for the admin panel.
 *
 * TWO UNITS, never converted into each other here:
 *
 *   * COINS — the user's spending wallet. Whole integers, shown as they arrive
 *     (`amountCoins`, `balanceCoins`, `totalChargedCoins`). There is NO fixed
 *     coins↔₹ ratio: nothing in this file divides paise by a coin rate.
 *   * PAISE — real money (package prices, listener earnings, legacy wallet
 *     history). Arrives as a paise string/int, shown as ₹, entered as ₹.
 */

export const PAISE_PER_RUPEE = 100;

const grouped = (n: number) => n.toLocaleString('en-IN');

/** 1990 → "1,990 coins". */
export function formatCoins(n: number): string {
  return `${grouped(n)} ${Math.abs(n) === 1 ? 'coin' : 'coins'}`;
}

/** Signed ledger movement: 40 → "+40 coins", -40 → "-40 coins", 0 → "0 coins". */
export function formatSignedCoins(n: number): string {
  if (n > 0) return `+${formatCoins(n)}`;
  if (n < 0) return `-${formatCoins(-n)}`;
  return formatCoins(0);
}

/** Paise (string or number) → "₹12.34". Null/blank/garbage → "—". */
export function formatRupees(paise: string | number | null | undefined): string {
  if (paise === null || paise === undefined || paise === '') return '—';
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  return `₹${(n / PAISE_PER_RUPEE).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/** Paise → rupees number for an input field (433 → 4.33). */
export function paiseToRupees(paise: string | number | null | undefined): number {
  const n = Number(paise ?? 0);
  return Number.isFinite(n) ? n / PAISE_PER_RUPEE : 0;
}

/**
 * Rupees typed by an operator → integer paise for the wire (₹7.80 → 780).
 * Rounded, so float noise like 19.99 * 100 = 1998.9999… still lands on 1999.
 */
export function rupeesToPaise(rupees: number | null | undefined): number {
  if (rupees === null || rupees === undefined || !Number.isFinite(rupees)) return 0;
  return Math.round(rupees * PAISE_PER_RUPEE);
}

/**
 * What a call cost the caller. `totalChargedCoins` is authoritative; the ₹ of
 * `totalChargedPaise` is only a fallback for rows from an API that predates it.
 */
export function formatCallCharge(row: {
  totalChargedCoins?: number | null;
  totalChargedPaise?: string | null;
}): string {
  if (typeof row.totalChargedCoins === 'number') return formatCoins(row.totalChargedCoins);
  return formatRupees(row.totalChargedPaise);
}

/**
 * One ledger row's amount, in its own unit. Coin rows carry a SIGNED
 * `amountCoins`; paise rows (listener earnings, legacy wallet, platform
 * accounts) carry an unsigned `amountPaise` plus a direction.
 */
export function formatLedgerAmount(row: {
  amountCoins?: number | null;
  amountPaise?: string | null;
  direction?: string;
}): string {
  if (typeof row.amountCoins === 'number') return formatSignedCoins(row.amountCoins);
  const r = formatRupees(row.amountPaise);
  if (r === '—') return r;
  if (row.direction === 'credit') return `+${r}`;
  if (row.direction === 'debit') return `-${r}`;
  return r;
}

/** Human labels for ledger reason codes. Unknown codes fall through verbatim. */
export const LEDGER_REASON_LABELS: Record<string, string> = {
  recharge: 'Recharge',
  recharge_bonus: 'Recharge bonus',
  recharge_refund: 'Recharge refund',
  call_charge: 'Call charge',
  call_refund: 'Call refund',
  call_earning: 'Call earning',
  call_platform_fee: 'Call platform fee',
  call_free_trial_comp: 'Free-trial compensation',
  chat_charge: 'Chat charge',
  chat_earning: 'Chat earning',
  gift_sent: 'Gift sent',
  party_gift: 'Party gift',
  referral_bonus: 'Referral bonus',
  reward_claim: 'Reward claim',
  daily_bonus: 'Daily Welcome Bonus',
  admin_adjust: 'Admin adjustment',
  admin_credit: 'Admin credit',
  admin_credit_reversal: 'Admin credit reversal',
  employee_work_earning: 'Employee work earnings',
  employee_work_reversal: 'Employee work reversal',
  coin_expiry: 'Coin expiry',
  wallet_migration: 'Wallet migration opening balance',
  agency_commission: 'Agency commission',
  agency_commission_reversal: 'Agency commission reversed (refund)',
  agency_payout_pending: 'Agency payout requested',
  agency_payout_settled: 'Agency payout paid',
  agency_payout_reversed: 'Agency payout rejected',
  agency_tds: 'Agency payout TDS',
};

export function ledgerReasonLabel(code: string, direction?: string): string {
  // One reason code covers both directions of a manual adjustment; the direction
  // is what tells an admin credit apart from an admin debit.
  if (code === 'admin_adjust' && direction === 'credit') return 'Admin credit';
  if (code === 'admin_adjust' && direction === 'debit') return 'Admin debit';
  return LEDGER_REASON_LABELS[code] ?? code;
}
